<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('MAKE_BILLS_PAYMENT')"
            :label="$t('Pay a Bill')"
            size="md"
            class="mt-0"
            @click="goToInitiateBillsPayment"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <b-dropdown
              v-if="getUserPermission('EXPORT_BILLS_PAYMENT_HISTORY')"
              id="dropdown-1"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              :text="$t('Export')"
              class="mr-1"
              variant="primary"
            >
              <b-dropdown-item @click="csvExport()">
                {{ $t('CSV') }}
              </b-dropdown-item>
              <b-dropdown-item @click="excelExport()">
                {{ $t('Excel') }}
              </b-dropdown-item>
            </b-dropdown>
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="billsPaymentRef"
      :items="billsPaymentList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(date)="data">
        {{ data.value ? toDateWithTimes(data.value) : '' }}
      </template>
      <template #cell(lastTSQCheck)="data">
        {{ data.value ? toDateWithTimes(data.value) : '' }}
      </template>
      <template #cell(dateReversed)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>

      <template #cell(amount)="data">
        {{ toBalance(data.value) }}
      </template>
      <template #cell(vendStatus)="data">
        <b-badge
          v-if="data.value"
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <template #cell(isReversed)="data">
        <b-badge
          v-if="data.value"
          pill
          :variant="getStatusColor(data.value === true ? 'Yes' : 'No')"
        >
          {{ data.value === true ? $t('Yes') : $t('No') }}
        </b-badge>
      </template>
      <!-- Column: Actions -->
      <template
        #cell(actions)="data"
      >
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template
              #button-content
            >
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="data.item.aggregatorResponseCode !== '00'"
              @click="onSelectBillsPaymentItem(data.item)"
            >
              <feather-icon icon="RefreshCcwIcon" />
              <span class="align-middle ml-50"> {{ $t('Verify Payment') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.billType === 'Electricity'"
              @click="onDownloadElectricityReceipt(data.item)"
            >
              <feather-icon icon="DownloadIcon" />
              <span class="align-middle ml-50"> {{ $t('Download Receipt') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-else
              @click="onDownloadReceipt(data.item)"
            >
              <feather-icon icon="DownloadIcon" />
              <span class="align-middle ml-50"> {{ $t('Download Receipt') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchBillsPayment"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.requestingAccountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="customer-name"
            v-model="filter.customerName"
            :label="$t('Customer Name')"
            :placeholder="$t('Enter customer name')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="package-name"
            v-model="filter.packageName"
            :label="$t('Package Name')"
            :placeholder="$t('Enter package name')"
            type="text"
          />
        </b-col>

        <b-col md="6">
          <SelectField
            id="initiated-by"
            v-model="filter.initiatedBy"
            :label="$t('Initiated By')"
            :options="userOptions"
            :placeholder="$t('Select')"
            :reduce="option => option.value"
            item="label"
            :loading="isLoading"
          />
        </b-col>

        <!-- Bill Type -->
        <b-col md="6">
          <SelectField
            id="bill-type"
            v-model="filter.billType"
            :label="$t('Bill Type')"
            :placeholder="$t('Select bill type')"
            :options="billTypeOptions"
            :reduce="option => option.value"
            item="label"
            :set-selected="setBillerType"
          />
        </b-col>
        <!-- Bill Type -->

        <b-col md="6">
          <SelectField
            id="biller"
            v-model="filter.billerIdentifier"
            :label="$t('Biller')"
            :placeholder="$t('Select biller')"
            :options="selectedBillers"
            :reduce="option => option.value"
            :readonly="isBillerDisabled"
            item="label"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="payment-reference"
            v-model="filter.paymentReference"
            :label="$t('Payment Reference')"
            :placeholder="$t('Enter Payment Reference')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="posting-reference"
            v-model="filter.postingRefernce"
            :label="$t('Posting Reference')"
            :placeholder="$t('Enter Posting Reference')"
            type="text"
          />
        </b-col>
        <!-- Transaction Date From -->
        <b-col md="6">
          <DatePickerField
            id="transaction-date-from"
            v-model="filter.startDate"
            :label="$t('Transaction Date (From)')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Transaction Date From-->

        <!-- Transaction Date To -->
        <b-col md="6">
          <DatePickerField
            id="transaction-date-to"
            v-model="filter.endDate"
            :label="$t('Transaction Date (To)')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Transaction Date To-->

        <b-col md="6">
          <InputField
            id="transaction-id"
            v-model="filter.transactionId"
            :label="$t('Transaction Id')"
            :placeholder="$t('Enter transaction Id')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="channel"
            v-model="filter.channel"
            :label="$t('Channel')"
            :placeholder="$t('Select')"
            :options="translatedChannelOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.vendStatus"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="translatedStatusOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Status -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable,
  BSpinner, BBadge, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import Ripple from 'vue-ripple-directive'
import * as FileSaver from 'file-saver'
import TablePagination from '@/views/components/table/TablePagination.vue'
import getStatusColor from '../../../../helpers/getStatusColor'
import {
  toAmount, toBalance, toFormatAmount, formatAmount,
} from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import {
  toDateWithYear, toDateWithTime, toDateTimeFormat, toDateWithTimes,
} from '../../../../helpers/formatDate'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import Button from '../../../components/button/Button.vue'
import { eChannelsPermission } from '../../../../helpers/permissions'

const statusOptions = [
  { value: 'AWAITING_SERVICE_PROVIDER', label: 'Awaiting Service Provider' },
  { value: 'AWAITING_TSQ', label: 'Awaiting TSQ' },
  { value: 'CANCELLED_BEFORE_EXECUTION', label: 'Cancelled Before Execution' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PENDING_PROCESSING', label: 'Pending Processing' },
  { value: 'REVERSED', label: 'Reversed' },
  { value: 'STAGED_FOR_REVERSAL', label: 'Staged for Reversal' },
]

const channelOptions = [
  { value: 'MOBILE', label: 'Mobile' },
  { value: 'POS', label: 'POS' },
  { value: 'WEB', label: 'Web' },
  { value: 'ATM', label: 'ATM' },
  { value: 'INTERNET_BANKING', label: 'Internet Banking' },
  { value: 'CORPORATE_IBANKING', label: 'Corporate Internet Banking' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CASH_DEPOSIT', label: 'Cash Deposit' },
  { value: 'USSD', label: 'USSD' },
  { value: 'CHAT_BOT', label: 'Chat Bot' },
  { value: 'KIOSK', label: 'Kiosk' },
  { value: 'OTHERS', label: 'Others' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    BBadge,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    FullScreenSpinner,
    DatePickerField,
    BDropdown,
    BDropdownItem,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      toDateWithTime,
      toDateWithTimes,
      toDateTimeFormat,
      getStatusColor,
      toAmount,
      toFormatAmount,
      formatAmount,
      toBalance,
      isBusy: false,
      showSpinner: false,
      isBillerDisabled: true,
      isLoadingInitiatorName: false,
      usersOptions: [],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'requestingAccountNumber', sortable: true, label: 'Account Number' },
        { key: 'requestingAccountName', sortable: true, label: 'Account Name' },
        { key: 'billType', sortable: true, label: 'Bill Type' },
        { key: 'billerIdentifier', sortable: true, label: 'Biller' },
        { key: 'packageName', sortable: true, label: 'Package Name' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'token', sortable: true, label: 'Token' },
        { key: 'channel', sortable: true, label: 'Channel' },
        { key: 'customerId', sortable: true, label: 'Customer Id' },
        { key: 'paymentReference', sortable: true, label: 'Payment Reference' },
        { key: 'postingRefernce', sortable: true, label: 'Posting Reference' },
        { key: 'postingResponseMessage', sortable: true, label: 'CBA Response' },
        { key: 'transactionId', sortable: true, label: 'Transaction Id' },
        { key: 'initiatedBy', sortable: true, label: 'Initiated By' },
        { key: 'aggregatorResponseCode', sortable: true, label: 'Vend Response Code' },
        { key: 'vendStatus', sortable: true, label: 'Vend Status' },
        { key: 'isReversed', sortable: true, label: 'Is Reversed' },
        { key: 'dateReversed', sortable: true, label: 'Date Reversed' },
        { key: 'date', sortable: true, label: 'Date Created' },
        { key: 'lastTSQCheck', sortable: true, label: 'Last TSQ Check' },
        { key: 'actions', sortable: true, label: 'Actions' },
      ],
      // statusOptions: [
      //   { value: 'AWAITING_SERVICE_PROVIDER', label: 'Awaiting Service Provider' },
      //   { value: 'AWAITING_TSQ', label: 'Awaiting TSQ' },
      //   { value: 'CANCELLED_BEFORE_EXECUTION', label: 'Cancelled Before Execution' },
      //   { value: 'CONFIRMED', label: 'Confirmed' },
      //   { value: 'EXPIRED', label: 'Expired' },
      //   { value: 'FAILED', label: 'Failed' },
      //   { value: 'PENDING', label: 'Pending' },
      //   { value: 'PENDING_PROCESSING', label: 'Pending Processing' },
      //   { value: 'REVERSED', label: 'Reversed' },
      //   { value: 'STAGED_FOR_REVERSAL', label: 'Staged for Reversal' },
      // ],
      // channelOptions: [
      //   { value: 'MOBILE', label: 'Mobile' },
      //   { value: 'POS', label: 'POS' },
      //   { value: 'WEB', label: 'Web' },
      //   { value: 'ATM', label: 'ATM' },
      //   { value: 'INTERNET_BANKING', label: 'Internet Banking' },
      //   { value: 'CORPORATE_IBANKING', label: 'Corporate Internet Banking' },
      //   { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
      //   { value: 'CASH_DEPOSIT', label: 'Cash Deposit' },
      //   { value: 'USSD', label: 'USSD' },
      //   { value: 'CHAT_BOT', label: 'Chat Bot' },
      //   { value: 'KIOSK', label: 'Kiosk' },
      //   { value: 'OTHERS', label: 'Others' },
      // ],

      billTypeOptions: [],
      billsPaymentList: [],
      filter: {
        requestingAccountNumber: null,
        billType: null,
        billerIdentifier: null,
        customerName: null,
        packageName: null,
        startDate: null,
        endDate: null,
        vendStatus: null,
        initiatedBy: null,
        paymentReference: null,
        postingRefernce: null,
        transactionId: null,
        channel: null,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
      },
      bankListOptions: [],
      allSearchedData: [],
      userOptions: [],
      selectedBillers: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      corporateDetails: 'operations/getCorporateDetails',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map((item, index) => ({
        'S/N': index + 1,
        'Account Number': item.requestingAccountNumber,
        'Account Name': item.requestingAccountName,
        'Bill Type': item.billType,
        'Biller Identifier': item.billerIdentifier,
        'Package Name': item.packageName,
        Amount: item.amount === '' ? '' : formatAmount(item.amount),
        Channel: item.channel,
        'Customer ID': item.customerId,
        'Payment Reference': item.paymentReference,
        'Posting Reference': item.postingRefernce,
        'Posting Response Message': item.postingResponseMessage,
        Date: item.date ? toDateWithYear(item.date) : '',
        'Is Reversed': item.isReversed,
        'Date Reversed': item.dateReversed ? toDateWithYear(item.dateReversed) : '',
        'Transaction Id': item.transactionId,
        'Vend Status': item.vendStatus,
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedStatusOptions() {
      return statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedChannelOptions() {
      return channelOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchBillsPayment()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchBillsPayment()
    },
  },
  mounted() {
    this.fetchBillsPayment()
    this.fetchBillTypes()
    this.fetchUsers()
  },
  methods: {
    ...mapActions({
      fetchBillsPayments: 'eChannels/fetchBillsPaymentHistory',
      getBillTypes: 'eChannels/getBillTypeList',
      verifyBillsPayment: 'eChannels/verifyBillsPaymentAction',
      getUsers: 'admin/getUserList',
      downloadTransactionReceipt: 'eChannels/downloadTransactionReceipt',
      downloadElectricityReceipt: 'eChannels/downloadElectricityReceipt',
      downloadBillsPaymentReport: 'eChannels/downloadBillsPaymentReport',
    }),
    async onSelectBillsPaymentItem(item) {
      try {
        this.showSpinner = true
        const formData = {
          transactionReference: item.paymentReference,
        }
        const response = await this.verifyBillsPayment({
          formData,
          permission: 'VIEW_BILLS_PAYMENT_HISTORY',
        })
        const { data, status } = response
        if (status === 200 && data) {
          const statusMessage = this.$t('This Bill Payment Transaction is')
          this.alertSuccess({ text: `${statusMessage} ${data.vendStatus}` })
          this.fetchBillsPayment()
          this.showSpinner = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async onDownloadReceipt(item) {
      try {
        this.isBusy = true
        const responseType = 'blob'
        const response = await this.downloadTransactionReceipt({ paymentReference: item.paymentReference, responseType, permission: 'VIEW_BILLS_PAYMENT_HISTORY' })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/pdf' })
          FileSaver.saveAs(blob, 'Transaction Receipt.pdf')
          this.alertSuccess({ text: 'Your request to download Transaction Receipt was successful' })
        }
      } catch (error) {
        const errorString = JSON.parse(await error.response.data.text())
        this.alertError({ text: `Your request to download Transaction Receipt failed. Reason: ${errorString.errors[0].errorMessage}` })
      } finally {
        this.isBusy = false
      }
    },
    async onDownloadElectricityReceipt(item) {
      try {
        this.isBusy = true
        const responseType = 'blob'
        const response = await this.downloadElectricityReceipt({ transactionID: item.paymentReference, responseType, permission: 'VIEW_BILLS_PAYMENT_HISTORY' })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/pdf' })
          FileSaver.saveAs(blob, 'Transaction Receipt.pdf')
          this.alertSuccess({ text: 'Your request to download Transaction Receipt was successful' })
        }
      } catch (error) {
        const errorString = JSON.parse(await error.response.data.text())
        this.alertError({ text: `Your request to download Transaction Receipt failed. Reason: ${errorString.errors[0].errorMessage}` })
      } finally {
        this.isBusy = false
      }
    },
    goToInitiateBillsPayment() {
      this.$router.push('initiate-bills-payment')
    },
    setBillerType(biller) {
      this.filter.billerIdentifier = ''
      const setBillers = this.allBillers.filter(item => item.billType === biller)
      this.selectedBillers = setBillers[0].billers.map(item => ({
        label: item.name,
        value: item.identifier,
      }))
      if (this.selectedBillers.length > 0) {
        this.isBillerDisabled = false
      } else {
        this.isBillerDisabled = true
      }
    },
    async fetchUsers() {
      try {
        this.isBusy = true
        const response = await this.getUsers({
          status: 'Active',
          permission: 'VIEW_BILLS_PAYMENT_HISTORY',
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.userOptions = data.data.map(items => ({
            label: `${items.firstname} ${items.lastname}`,
            value: items.email,
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchBillsPayment() {
      try {
        this.isBusy = true
        const response = await this.fetchBillsPayments({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: 'VIEW_BILLS_PAYMENT_HISTORY',
        })
        const { status, data } = response
        if (status === 200) {
          this.isBusy = false
          this.billsPaymentList = data.data
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async fetchBillTypes() {
      try {
        const response = await this.getBillTypes({
          permission: 'VIEW_BILLS_PAYMENT_HISTORY',
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.allBillers = data.data
          this.billTypeOptions = data.data.map(item => ({
            label: item.name,
            value: item.billType,
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadBillsPaymentReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 1,
          permission: eChannelsPermission.EXPORT_BILLS_PAYMENT_HISTORY,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadBillsPaymentReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 0,
          permission: eChannelsPermission.EXPORT_BILLS_PAYMENT_HISTORY,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filter = {
        ...null,
      }
    },
    resetFilter() {
      this.filter = {
        ...null,
      }
      this.fetchBillsPayment()
    },

  },
}
</script>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
      @media screen {
  #prints {
    display: none;
   }
}

@media print {
   html, body {
     height: 99%;
     page-break-after: avoid !important;
     page-break-before: avoid !important;
     visibility:hidden;
  }
  #transaction-printer, #transaction-printer * {
    visibility: visible; border: none;
  }
  #prints, #prints * {
    visibility:visible;
  }
  #prints {
    position:absolute;
    left:0;
    top:0;
    right: 0;
  }
}
img {
  max-width: 100%;
  height: auto;
}
.center-image {
  border: 5px solid;
  margin: auto;
  width: 25%;

}
.watermark {
    position: relative;
}

.watermark__inner {
    /* Center the content */
    align-items: center;
    display: flex;
    justify-content: center;

    /* Absolute position */
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
}

.watermark__body {
    color: rgba(0, 0, 0, 0.2);
    font-size: 9rem;
    font-weight: bold;
    white-space: nowrap;
    text-transform: capitalize;
    font-family: "Gill Sans Extrabold", sans-serif !important;
    /* Rotate the text */
    transform: rotate(-45deg);

    /* Disable the selection */
    user-select: none;
}
</style>
